import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {InputSharedStylesHTML} from '../shared-styles/input.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';

class KeliCheckbox extends ScopeBinding(InputBehavior(PolymerElement)) {
  static get template() {
    return html`
      ${InputSharedStylesHTML}
      <style>
        :host {
          padding: 0;
          height: auto;
          min-height: var(--keli-input-field-min-height, 40px);
        }

        :host(.help-icon-visible) {
          padding-right: .8em;
        }

        .hidden {
          display: none;
          visibility: hidden;
        }

        #input {
          display: block;
          font-weight: 300;
          font-size: var(--keli-input-field-font-size, 1em);
          background-color: transparent;
          height: auto;
          color: var(--keli-field-label-color);
        }

        :host(.focused) #input {
          color: var(--keli-field-label-color-active);
        }

        :host(.disabled) #input {
          background-color: transparent;
          --sl-input-background-color: var(--keli-field-disabled-background);
        }

        #help {
          position: absolute;
          right: 0;
          font-size: .7em;
          top: 0;
          color: var(--keli-text-subtle);
          cursor: help;
        }

        #help:hover {
          color: var(--keli-text);
        }

        #shortcut {
          width: var(--sl-toggle-size-medium);
          text-align: center;
          top: calc(var(--sl-toggle-size-medium) * 1.6);
          left: 0;
          right: auto;
        }

        #input::part(base) {
          align-items: start;
          opacity: 1; /* Prevent disabled style */
        }
      </style>

      <sl-checkbox id="input" disabled$="[[disabled]]" on-sl-change="checkedChanged">[[caption]]</sl-checkbox>
      <sl-icon id="help" name="help-circle-outline" class$="[[_computeHelpClass(helpText, chrome, _initialized)]]"></sl-icon>
      <span id="shortcut" class$="[[_computeShortcutClass(formattedShortcutKey)]]">[[formattedShortcutKey]]</span>`;
  }

  static get is() {
    return 'keli-checkbox';
  }

  static get properties() {
    return {
      _initialized: {type: Boolean},
      keliControlTypes: {type: Array, value: ['boolean']},
      selectOnFocus: {type: Boolean, value: false},
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._initialized = true;
  }

  _computeHelpClass(helpText, chrome) {
    this.classList.toggle('help-icon-visible', (helpText && chrome));

    const classList = [];
    if (!helpText || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeShortcutClass(formattedShortcutKey) {
    const classList = [];
    if (!formattedShortcutKey) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  displayValueChanged(newValue) {
    afterNextRender(this.$.input, () => {
      if (this.textValueToChecked(this.displayValue) === this.textValueToChecked(newValue)) {
        this.$.input.checked = this.textValueToChecked(newValue);
      }
    });
  }

  textCasingChanged() {
    return false;
  }

  textValueToChecked(value) {
    if (typeof value === 'boolean') {
      return value;
    }
    return !!(value && ['t', 'T', 'y', 'Y', 'true', 'on'].indexOf(value) !== -1);
  }

  focusInput() {
    // Try the fast method first, but fall back to afterNextRender if the checkbox element isn't ready yet.
    if (this.$.input?.focus) {
      this.$.input.focus();
    } else {
      afterNextRender(this.$.input, () => {
        this.$.input.focus();
      });
    }
  }

  handleInput() {
    // Prevent inherited input event behavior
    return false;
  }

  checkedChanged() {
    const newValue = this.$.input.checked;
    if (!this.isConnected || newValue === undefined || newValue === this.textValueToChecked(this.displayValue)) {
      return;
    }
    this.currentValue = newValue;

    this.dispatchEvent(new CustomEvent('keliUserChange', {
      bubbles: true,
      composed: true,
      detail: {element: this, oldValue: this.fieldInputOldValue, newValue: this.currentValue}
    }));
    this.fieldInputOldValue = this.currentValue;

    this.fireKeliAccepted = true;
    this._keliAccept();
  }

  validate() {
    // Integrate with Shoelace's validation.
    if (this.$.input.input && this.$.input.checkValidity() === false) {
      this.validityState = {valid: false, message: this.errorMessage || this.defaultErrorMessage, fromUI: true};
    } else {
      this.validityState = {valid: true};
    }
    return false;
  }

  toRawFieldValue(value) {
    return this.textValueToChecked(value);
  }
}

customElements.define(KeliCheckbox.is, KeliCheckbox);
export {KeliCheckbox};
